import React, { useMemo, useState } from 'react';

import { PaginationOptionsChangePayload, RowsPerPageOption } from '../types';

import Input from '../../../components/input';
import Select from '../../../components/select';
import Pagination from '../../../components/pagination';
import { Placement } from '../../../components/overlay';

import { getRowsPerPageOptions } from '../utils/get-rows-per-page-options';

type IProps = {
  page: number;
  limit: number;
  totalRows: number;
  customOptions: (25 | 50 | 100 | 250 | 500 | 1000)[];
  onPaginationOptionsChange: (payload: PaginationOptionsChangePayload) => void;
};

const TablePagination: React.FC<IProps> = ({
  page,
  limit,
  totalRows,
  onPaginationOptionsChange,
  customOptions = [],
}) => {
  const [goToPage, setGoToPage] = useState(1);

  const rowsPerPageOptions = useMemo(
    () => getRowsPerPageOptions(customOptions),
    [customOptions],
  );

  const totalPages = useMemo(() => {
    const totalPagesCount = Math.ceil(totalRows / limit);

    if (page > totalPagesCount) {
      console.log("Case: Current page shouldn't be greater than total pages");
      onPaginationOptionsChange({ page: 1, limit });
    }

    return totalPagesCount;
  }, [totalRows, limit]);

  const onGoToPageValueChange = (value: string) => {
    setGoToPage(Number(value));
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    onPaginationOptionsChange({ page: goToPage, limit });
  };

  useMemo(() => {
    setGoToPage(page);
  }, [page]);

  return (
    <div className="shd-table--pagination">
      <div className="shd-table--pagination__container">
        <p className="shd-table--pagination__text">Showing</p>
        <Select<RowsPerPageOption>
          className="shd-table--pagination__select"
          options={rowsPerPageOptions}
          selectedOptionKey={limit.toString()}
          selectedOptionRenderer={([option]) => (
            <span>{option?.value > totalRows ? totalRows : option?.value}</span>
          )}
          optionRenderer={(option) => <span>{option?.value}</span>}
          onChange={([option]) =>
            onPaginationOptionsChange({ page, limit: option.value })
          }
          placeholder="Select Limit"
          placement={Placement.Top}
        />
        <p className="shd-table--pagination__text">{`out of ${totalRows}`}</p>
      </div>

      <div className="shd-table--pagination__separator" />

      <div className="shd-table--pagination__container">
        <p className="shd-table--pagination__text">Go to page:</p>

        <div className="shd-table--pagination__input">
          <form onSubmit={handleGoToPage}>
            <Input
              type="number"
              value={goToPage}
              onChange={onGoToPageValueChange}
              min={1}
              max={totalPages}
            />
          </form>
        </div>
        <p className="shd-table--pagination__text">{`/${totalPages}`}</p>
      </div>

      {page <= totalPages && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onChange={(newPage) => {
            onPaginationOptionsChange({ page: newPage, limit });
          }}
        />
      )}
    </div>
  );
};

export default TablePagination;
